import { LEVEL_CONFIG } from '../config/levels.js';
import { getGamepads } from '../config/controls.js';

// Track input type per player
let playerInputTypes = { 1: 'keyboard', 2: 'keyboard' };

// Update the HUD with current game state
export function updateHUD(players, boss, currentLevel, minions = []) {
    updatePlayerInputTypes(players);
    updateHealthBars(players, boss);
    updateMinionHealthBars(currentLevel, minions);
}

// Detect what input each player is using
function updatePlayerInputTypes(players) {
    const pads = getGamepads();

    players.forEach((player, index) => {
        const playerNum = index + 1;
        const pad = pads[index];

        // Check if gamepad has any input
        if (pad && pad.connected) {
            const hasInput = pad.axes.some(a => Math.abs(a) > 0.1) ||
                           pad.buttons.some(b => b.pressed);
            if (hasInput) {
                playerInputTypes[playerNum] = 'gamepad';
            }
        }
    });
}

// Set input type for a player (called from input system)
export function setPlayerInputType(playerNum, type) {
    playerInputTypes[playerNum] = type;
    updatePlayerLabel(playerNum);
}

// Update a single player's label with input icon
function updatePlayerLabel(playerNum) {
    const containerId = playerNum === 1 ? 'player1HealthContainer' : `player${playerNum}HealthContainer`;
    let container = document.getElementById(containerId);

    // For player 1, it might be in the original structure
    if (!container && playerNum === 1) {
        const statusEl = document.getElementById('status');
        if (statusEl) {
            container = statusEl.querySelector('.health-container');
        }
    }

    if (container) {
        const label = container.querySelector('.player-label');
        if (label) {
            const icon = playerInputTypes[playerNum] === 'gamepad' ? '🎮' : '⌨️';
            const heart = playerNum === 1 ? '❤️' : '💙';
            label.innerHTML = `${heart} P${playerNum} ${icon}`;
        }
    }
}

// Update health bars
function updateHealthBars(players, boss) {
    // Player health bars
    players.forEach((player, index) => {
        const healthBarId = index === 0 ? 'playerHealthBar' : `player${index + 1}HealthBar`;
        const healthBar = document.getElementById(healthBarId);

        if (healthBar) {
            const percent = Math.max(0, (player.health / player.maxHealth) * 100);
            healthBar.style.width = percent + '%';
        }

        // Update input type icon
        updatePlayerLabel(index + 1);
    });

    // Boss health bar
    const bossHealthBar = document.getElementById('bossHealthBar');
    if (bossHealthBar && boss) {
        const percent = Math.max(0, (boss.health / boss.maxHealth) * 100);
        bossHealthBar.style.width = percent + '%';
    }
}

// Update minion health bars
function updateMinionHealthBars(currentLevel, minions) {
    let minionContainer = document.getElementById('minionHealthContainer');

    if (currentLevel >= 3 && minions.length > 0) {
        // Create container if needed
        if (!minionContainer) {
            minionContainer = document.createElement('div');
            minionContainer.id = 'minionHealthContainer';
            minionContainer.style.cssText = `
                position: fixed;
                top: 10px;
                right: 240px;
                background: rgba(0,0,0,0.8);
                padding: 10px 15px;
                border-radius: 10px;
                z-index: 1000;
                min-width: 120px;
            `;
            document.body.appendChild(minionContainer);
        }

        // Build minion health bars HTML
        let html = '<div style="color: #ff66ff; font-weight: bold; margin-bottom: 8px;">👾 MINIONS</div>';

        minions.forEach((minion, i) => {
            const percent = (minion.health / minion.maxHealth) * 100;
            html += `
                <div style="margin: 4px 0;">
                    <div style="height: 8px; background: #333; border-radius: 4px; overflow: hidden;">
                        <div style="height: 100%; width: ${percent}%; background: linear-gradient(90deg, #9C27B0, #E91E63); border-radius: 4px; transition: width 0.2s;"></div>
                    </div>
                </div>
            `;
        });

        minionContainer.innerHTML = html;
        minionContainer.style.display = 'block';
    } else {
        if (minionContainer) {
            minionContainer.style.display = 'none';
        }
    }
}

// Update level display
export function updateLevelDisplay(currentLevel) {
    let levelDisplay = document.getElementById('levelDisplay');
    if (!levelDisplay) {
        levelDisplay = document.createElement('div');
        levelDisplay.id = 'levelDisplay';
        levelDisplay.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            color: white;
            font-size: 18px;
            font-weight: bold;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            z-index: 1000;
        `;
        document.body.appendChild(levelDisplay);
    }

    const config = LEVEL_CONFIG[currentLevel];
    levelDisplay.innerHTML = `Level ${currentLevel}: ${config.name}`;
}

// Update controller status
export function updateControllerStatus(status) {
    // No longer showing in HUD, but keep function for compatibility
}

// Show controller connected message
export function showControllerMessage(controllerName = 'Controller') {
    const msg = document.createElement('div');
    msg.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, #4CAF50, #8BC34A);
        color: white;
        padding: 15px 25px;
        border-radius: 25px;
        font-size: 18px;
        font-weight: bold;
        z-index: 10000;
        animation: slideIn 0.5s ease;
    `;
    msg.textContent = `🎮 ${controllerName} Connected!`;
    document.body.appendChild(msg);

    setTimeout(() => {
        msg.style.opacity = '0';
        msg.style.transition = 'opacity 0.5s';
        setTimeout(() => msg.remove(), 500);
    }, 2000);
}

// Clear defeated overlays from health bars
export function clearDefeatedOverlays() {
    const overlays = document.querySelectorAll('.defeated-overlay');
    overlays.forEach(overlay => overlay.remove());
}

// Setup HUD for player count
export function setupHUD(playerCount) {
    const statusEl = document.getElementById('status');
    if (!statusEl) return;

    // Clear any defeated overlays from previous game
    clearDefeatedOverlays();

    // Reset input types
    playerInputTypes = { 1: 'keyboard', 2: 'keyboard' };

    // Update first player container with ID and label class
    const firstContainer = statusEl.querySelector('.health-container');
    if (firstContainer && !firstContainer.id) {
        firstContainer.id = 'player1HealthContainer';
        const label = firstContainer.querySelector('div');
        if (label) {
            label.className = 'player-label';
            const icon = playerInputTypes[1] === 'gamepad' ? '🎮' : '⌨️';
            if (playerCount === 2) {
                label.innerHTML = `❤️ P1 ${icon}`;
            } else {
                label.innerHTML = `❤️ PLAYER ${icon}`;
            }
        }
    }

    // If 2 players, add second health bar
    if (playerCount === 2) {
        const existingP2 = document.getElementById('player2HealthContainer');
        if (!existingP2) {
            const p2Container = document.createElement('div');
            p2Container.id = 'player2HealthContainer';
            p2Container.className = 'health-container';
            const icon = playerInputTypes[2] === 'gamepad' ? '🎮' : '⌨️';
            p2Container.innerHTML = `
                <div class="player-label">💙 P2 ${icon}</div>
                <div class="health-bar">
                    <div class="health-fill" id="player2HealthBar" style="background: linear-gradient(90deg, #2196F3, #03A9F4);"></div>
                </div>
            `;

            // Insert after first player health
            if (firstContainer) {
                firstContainer.insertAdjacentElement('afterend', p2Container);
            }
        }

        // Update P1 label for 2-player mode
        const p1Label = firstContainer?.querySelector('.player-label');
        if (p1Label) {
            const icon = playerInputTypes[1] === 'gamepad' ? '🎮' : '⌨️';
            p1Label.innerHTML = `❤️ P1 ${icon}`;
        }
    } else {
        // Remove P2 health bar if exists
        const p2Container = document.getElementById('player2HealthContainer');
        if (p2Container) {
            p2Container.remove();
        }

        // Reset P1 label for single player
        const p1Label = firstContainer?.querySelector('.player-label');
        if (p1Label) {
            const icon = playerInputTypes[1] === 'gamepad' ? '🎮' : '⌨️';
            p1Label.innerHTML = `❤️ PLAYER ${icon}`;
        }
    }

    // Clear minion container
    const minionContainer = document.getElementById('minionHealthContainer');
    if (minionContainer) {
        minionContainer.remove();
    }
}

// Update controls help for player count
export function updateControlsHelp(playerCount) {
    const infoEl = document.getElementById('info');
    if (!infoEl) return;

    if (playerCount === 2) {
        infoEl.innerHTML = `
            <div><strong>⌨️ PLAYER 1</strong></div>
            <div>WASD - Move | Space - Jump</div>
            <div>F - Attack | Shift - Dodge</div>
            <div style="margin-top: 10px;"><strong>⌨️ PLAYER 2</strong></div>
            <div>Arrow Keys - Move | Enter - Jump</div>
            <div>End - Attack | Ctrl - Dodge</div>
            <div style="margin-top: 10px;">H - Toggle help</div>
        `;
    } else {
        infoEl.innerHTML = `
            <div><strong>⌨️ CONTROLS</strong></div>
            <div>WASD - Move | Space - Jump</div>
            <div>F - Attack | Shift - Dodge</div>
            <div>H - Toggle this help</div>
        `;
    }
}

// Hide loading screen
export function hideLoading() {
    const loading = document.getElementById('loading');
    if (loading) {
        loading.style.display = 'none';
    }
}
